import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { homeFor } from './roleNav';
import type { Role } from '@/types/api';

function Booting() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas">
      <div className="flex items-center gap-2 text-ink-muted">
        <ShieldCheck className="size-5 animate-pulse text-accent" aria-hidden />
        <span className="text-sm">Loading AutoAware…</span>
      </div>
    </div>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <Booting />;

  if (status === 'anonymous') {
    // Preserve where they were headed so the login can send them back.
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  return <>{children}</>;
}

/**
 * Role gates mirror the backend's, but they are a navigation aid, not a security
 * boundary — the server enforces the real thing. Sending someone to their own home
 * page beats showing a page that will only ever render errors.
 */
export function RequireRole({ roles, children }: { roles: readonly Role[]; children: ReactNode }) {
  const { user, status } = useAuth();

  if (status === 'loading') return <Booting />;
  if (!user) return <Navigate to="/login" replace />;

  if (!roles.includes(user.role)) {
    return <Navigate to={homeFor(user.role)} replace />;
  }

  return <>{children}</>;
}

export function RedirectHome() {
  const { user } = useAuth();
  return <Navigate to={homeFor(user?.role ?? null)} replace />;
}

export function RedirectIfSignedIn({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <Booting />;

  if (status === 'authenticated') {
    const params = new URLSearchParams(location.search);
    const next = params.get('next');
    return <Navigate to={next ?? homeFor(user?.role ?? null)} replace />;
  }

  return <>{children}</>;
}
